import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { GradeBadge } from "@/components/GradeTooltip";
import { CardHoverZoom } from "@/components/CardHoverZoom";
import { PannableCard } from "@/components/PannableCard";
import { CardImage } from "@/components/CardImage";
import packFront from "@/assets/pack-front.png.asset.json";
import { RARITY_GLOW } from "@/lib/game-constants";
import {
  BINDER_CAPACITY,
  BINDER_PAGES,
  BINDER_SECTIONS,
  BINDER_SLOTS_PER_PAGE,
  sectionForCard,
  sectionForSlot,
  slotsOfSection,
  type PullStatus,
} from "@/lib/binder-shared";
import { OFFICIAL_CARD_BY_CODE } from "@/lib/tcg-cards";
import { createBinderShareLink, getMyCards, placeCardInSlot, setCardFavorite, setCardStatus } from "@/lib/binder.functions";
import { DEMO_BINDER, type DemoCard } from "@/lib/binder-demo";
import binderCover from "@/assets/binder-cover.jpg";
import { ShareButton } from "@/components/ShareButton";
import { useCardPrices } from "@/lib/use-card-prices";

import { useSession } from "@/lib/user-data";

export const Route = createFileRoute("/binder")({
  head: () => ({
    meta: [
      { title: "Your Palworld Card Binder — Pal Stars Collector Binder" },
      { name: "description", content: "File your Palworld card pulls into a 108-pocket collector binder sorted by color, drag cards between pockets, hold trades and share your binder." },
      { property: "og:title", content: "Your Palworld Card Binder" },
      { property: "og:description", content: "A nine-pocket collector binder for every Palworld card you pull on Pal Stars." },
      { property: "og:url", content: "https://palstars.com/binder" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex,follow" },
    ],
    links: [{ rel: "canonical", href: "https://palstars.com/binder" }],
  }),
  component: BinderPage,
});

export interface CardRow {
  id: string;
  card_code: string;
  card_name: string;
  rarity: string;
  image: string;
  grade: number;
  grade_label: string;
  centering?: number | null;
  corners?: number | null;
  edges?: number | null;
  surface?: number | null;
  /** Defects a grader noted on the label — surfaced on grade hover. */
  defects?: string[] | null;
  status: string;
  binder_slot: number | null;
  favorite: boolean;
  pulled_at: string;
}

/** Color block a card belongs to, from the official card list. */
function sectionOf(card: { card_code: string; rarity: string }) {
  const official = OFFICIAL_CARD_BY_CODE.get(card.card_code.toUpperCase());
  return sectionForCard(card.rarity, official?.color);
}

/** Faint pack-art placeholder for a pocket that has no card in it yet. */
export function EmptyPocket({
  slot,
  onPick,
  dragOver,
  dropProps,
}: {
  slot: number;
  onPick?: () => void;
  dragOver?: boolean;
  dropProps?: React.HTMLAttributes<HTMLElement>;
}) {
  const inner = (
    <>
      <img
        src={packFront.url}
        alt=""
        aria-hidden="true"
        loading="lazy"
        decoding="async"
        className="absolute inset-0 h-full w-full object-cover opacity-15 grayscale"
      />
      <span className="relative text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        {onPick ? `Place in #${slot + 1}` : `#${slot + 1} empty`}
      </span>
    </>
  );
  const cls =
    `relative flex aspect-[5/7] w-full items-center justify-center overflow-hidden rounded-lg border border-dashed bg-card/30 transition-colors ${
      dragOver ? "border-primary bg-primary/15" : "border-border/70"
    }`;
  if (onPick) {
    return (
      <button type="button" onClick={onPick} aria-label={`Place card in binder pocket ${slot + 1}`} {...dropProps} className={`${cls} border-primary/70 hover:bg-primary/10`}>
        {inner}
      </button>
    );
  }
  return (
    <div className={cls} aria-label={`Empty binder pocket ${slot + 1}`} {...dropProps}>
      {inner}
    </div>
  );
}

export function BinderGrid({
  page,
  cards,
  onExpand,
  onRemove,
  onTrade,
  onFavorite,
  onPickSlot,
  onHoldCard,
  onDropCard,
  isMatch,
}: {
  page: number;
  cards: Map<number, CardRow>;
  onExpand?: (c: CardRow) => void;
  onRemove?: (c: CardRow) => void;
  onTrade?: (c: CardRow) => void;
  /** Star / unstar the card, feeding the "Favourites only" filter. */
  onFavorite?: (c: CardRow) => void;
  onPickSlot?: (slot: number) => void;

  /** Press-and-hold a filled pocket (mobile) to start moving that card. */
  onHoldCard?: (slot: number) => void;
  /** Drag-and-drop / press-and-hold move of a card into an exact pocket. */
  onDropCard?: (cardId: string, slot: number) => void;
  /** Binder filters: cards that fail the predicate are dimmed, never hidden. */
  isMatch?: (c: CardRow) => boolean;
}) {
  const start = page * BINDER_SLOTS_PER_PAGE;
  const section = sectionForSlot(start);
  const [dragOver, setDragOver] = useState<number | null>(null);
  const holdRef = useRef<number | null>(null);


  const dropProps = (slot: number): React.HTMLAttributes<HTMLElement> =>
    onDropCard
      ? {
          onDragOver: (e) => { e.preventDefault(); setDragOver(slot); },
          onDragLeave: () => setDragOver((s) => (s === slot ? null : s)),
          onDrop: (e) => {
            e.preventDefault();
            setDragOver(null);
            const id = e.dataTransfer?.getData("text/plain");
            if (id) onDropCard(id, slot);
          },
        }
      : {};

  return (
    <div className="grid grid-cols-3 gap-3 sm:gap-4" style={{ ["--pocket-tint" as string]: section.tint }}>
      {Array.from({ length: BINDER_SLOTS_PER_PAGE }, (_, i) => {
        const slot = start + i;
        const card = cards.get(slot);
        return (
          <div key={slot}>
            {card ? (
              <div
                draggable={Boolean(onDropCard)}
                onDragStart={(e) => {
                  e.dataTransfer.setData("text/plain", card.id);
                  e.dataTransfer.effectAllowed = "move";
                }}
                onPointerDown={() => {
                  if (!onDropCard) return;
                  holdRef.current = window.setTimeout(() => onHoldCard?.(slot), 450);
                }}
                onPointerUp={() => { if (holdRef.current) window.clearTimeout(holdRef.current); }}
                onPointerLeave={() => { if (holdRef.current) window.clearTimeout(holdRef.current); }}
                {...dropProps(slot)}
                className={`rounded-lg p-0.5 transition-all ${onDropCard ? "cursor-grab active:cursor-grabbing" : ""} ${dragOver === slot ? "bg-primary/20" : ""} ${isMatch && !isMatch(card) ? "opacity-25 grayscale" : ""}`}
                style={{ boxShadow: `inset 0 0 0 1px ${section.tint}33` }}
              >
                <CardHoverZoom
                  src={card.image}
                  alt={`${card.card_name} ${card.card_code} ${card.rarity} Palworld card`}
                  width={180}
                  height={251}
                  scale={1.2}
                  onExpand={onExpand ? () => onExpand(card) : undefined}
                />
                <p className="mt-1 truncate text-[11px] font-semibold">{card.card_name}</p>
                <p className="text-[10px] text-muted-foreground">
                  <span style={{ color: RARITY_GLOW[card.rarity] }}>{card.rarity}</span> ·{" "}
                  <GradeBadge card={card} />
                </p>
                {(onTrade || onRemove || onFavorite) && (
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-[10px]">
                    {onFavorite && (
                      <button
                        type="button"
                        onClick={() => onFavorite(card)}
                        aria-pressed={card.favorite}
                        title={card.favorite ? "Remove from favourites" : "Mark as favourite"}
                        className={card.favorite ? "text-amber-400" : "text-muted-foreground hover:text-amber-400"}
                      >
                        {card.favorite ? "★ Favourite" : "☆ Favourite"}
                      </button>
                    )}
                    {onTrade && (
                      <button type="button" onClick={() => onTrade(card)} className="text-primary hover:underline">
                        To trade stack
                      </button>
                    )}
                    {onRemove && (
                      <button type="button" onClick={() => onRemove(card)} className="text-muted-foreground hover:text-destructive">
                        Discard
                      </button>
                    )}
                  </div>
                )}

              </div>
            ) : (
              <EmptyPocket
                slot={slot}
                onPick={onPickSlot ? () => onPickSlot(slot) : undefined}
                dragOver={dragOver === slot}
                dropProps={dropProps(slot)}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

/** Painted binder cover shown on the first page of the collection. */
function BinderCover({ filled }: { filled: number }) {
  return (
    <figure className="relative overflow-hidden rounded-2xl border border-primary/30">
      <img
        src={binderCover}
        alt="Pal Stars Palworld card binder cover artwork — Palpagos islands at sunrise"
        width={1536}
        height={1024}
        loading="lazy"
        decoding="async"
        className="h-40 w-full object-cover sm:h-56"
      />
      <figcaption className="absolute inset-0 flex flex-col justify-end bg-gradient-to-t from-background/95 via-background/40 to-transparent p-4 sm:p-6">
        <p className="text-[11px] uppercase tracking-widest text-primary">Pal Stars collector binder</p>
        <p className="text-lg font-bold sm:text-2xl">Dawn of Palpagos — {filled}/{BINDER_CAPACITY} pockets filled</p>
      </figcaption>
    </figure>
  );
}



function BinderPage() {
  const { session, loading } = useSession();
  const cardsFn = useServerFn(getMyCards);
  const mintShareLink = useServerFn(createBinderShareLink);
  const statusFn = useServerFn(setCardStatus);
  const placeFn = useServerFn(placeCardInSlot);
  const favoriteFn = useServerFn(setCardFavorite);


  const [rows, setRows] = useState<CardRow[]>([]);
  const [page, setPage] = useState(0);
  // Turning pages mid-drag: dragover fires continuously, so throttle the flip.
  const flipRef = useRef(0);
  const flipTo = useCallback((next: (p: number) => number) => {
    const now = Date.now();
    if (now - flipRef.current < 600) return;
    flipRef.current = now;
    setPage((p) => Math.min(BINDER_PAGES - 1, Math.max(0, next(p))));
  }, []);

  const [notice, setNotice] = useState<string | null>(null);
  const [zoom, setZoom] = useState<CardRow | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  /** Card the collector is currently placing into a chosen pocket. */
  const [placing, setPlacing] = useState<CardRow | null>(null);
  /** Last card move, so a mis-drop can be put straight back. */
  const [lastMove, setLastMove] = useState<
    { id: string; name: string; status: PullStatus; slot: number | null; swapped?: boolean } | null
  >(null);
  /** Binder filters — matching cards stay bright, the rest dim out. */
  const [fq, setFq] = useState("");
  const [fRarity, setFRarity] = useState("all");
  const [fGrade, setFGrade] = useState("all");
  const [fFav, setFFav] = useState(false);

  const refresh = useCallback(async () => {
    if (!session) return;
    try {
      setRows((await cardsFn({})) as unknown as CardRow[]);
    } catch (e) {
      setNotice(e instanceof Error ? e.message : "Could not load your cards.");
    }
  }, [session, cardsFn]);

  useEffect(() => { void refresh(); }, [refresh]);

  const unsorted = useMemo(() => rows.filter((r) => r.status === "new"), [rows]);
  const tradeStack = useMemo(() => rows.filter((r) => r.status === "trade"), [rows]);
  const binderMap = useMemo(() => {
    const m = new Map<number, CardRow>();
    rows.filter((r) => r.status === "binder" && r.binder_slot !== null).forEach((r) => m.set(r.binder_slot as number, r));
    return m;
  }, [rows]);
  const filled = binderMap.size;
  const complete = filled >= BINDER_CAPACITY;

  // Live market value of every card the member holds (binder + trade stack),
  // read from the same price board that powers /prices.
  const { byCode } = useCardPrices();
  const { netWorth, valuedCount } = useMemo(() => {
    let total = 0;
    let counted = 0;
    for (const r of rows) {
      const key = r.card_code.toUpperCase();
      const row = byCode.get(key) ?? byCode.get(key.replace(/(OSR|SSP|TSP|SR|RR|SP)$/, ""));
      const v = row?.market ?? null;
      if (typeof v === "number" && Number.isFinite(v)) {
        total += v;
        counted += 1;
      }
    }
    return { netWorth: total, valuedCount: counted };
  }, [rows, byCode]);

  const rarities = useMemo(
    () => Array.from(new Set(Array.from(binderMap.values()).map((c) => c.rarity))).sort(),
    [binderMap],
  );
  const filtersOn = fq.trim() !== "" || fRarity !== "all" || fGrade !== "all" || fFav;
  const isMatch = useCallback(
    (c: CardRow) => {
      const needle = fq.trim().toLowerCase();
      if (needle && !`${c.card_name} ${c.card_code}`.toLowerCase().includes(needle)) return false;
      if (fRarity !== "all" && c.rarity !== fRarity) return false;
      if (fGrade !== "all" && c.grade < Number(fGrade)) return false;
      if (fFav && !c.favorite) return false;
      return true;
    },
    [fq, fRarity, fGrade, fFav],
  );
  const matchCount = useMemo(
    () => (filtersOn ? Array.from(binderMap.values()).filter(isMatch).length : filled),
    [binderMap, filtersOn, isMatch, filled],
  );

  async function move(card: CardRow, status: PullStatus, slot: number | null = null) {
    setBusyId(card.id);
    setNotice(null);
    const prev = { id: card.id, name: card.card_name, status: card.status as PullStatus, slot: card.binder_slot };
    try {
      await statusFn({ data: { id: card.id, status, slot } });
      await refresh();
      setLastMove(prev);
      setNotice(
        status === "binder"
          ? `${card.card_name} filed into ${slot === null ? "your binder" : `pocket #${slot + 1}`}.`
          : status === "trade"
            ? `${card.card_name} stored for future trades.`
            : `${card.card_name} discarded.`,
      );
    } catch (e) {
      setNotice(e instanceof Error ? e.message : "That change did not save.");
    } finally {
      setBusyId(null);
      setPlacing(null);
    }
  }

  /** Star / unstar a card, with an optimistic flip so the grid reacts instantly. */
  async function toggleFavorite(card: CardRow) {
    const next = !card.favorite;
    setNotice(null);
    setRows((rs) => rs.map((r) => (r.id === card.id ? { ...r, favorite: next } : r)));
    try {
      await favoriteFn({ data: { id: card.id, favorite: next } });
      setNotice(next ? `${card.card_name} added to your favourites.` : `${card.card_name} removed from favourites.`);
    } catch (e) {
      setRows((rs) => rs.map((r) => (r.id === card.id ? { ...r, favorite: card.favorite } : r)));
      setNotice(e instanceof Error ? e.message : "That favourite did not save.");
    }
  }


  /** File a new pull into the first free pocket of its own color section. */
  async function fileInColorSection(card: CardRow) {
    const section = sectionOf(card);
    const free = slotsOfSection(section).find((s) => !binderMap.has(s));
    if (free === undefined) {
      setNotice(`The ${section.label} pages are full — choose any pocket instead.`);
      return;
    }
    await move(card, "binder", free);
  }

  /** Drag-and-drop (or press-and-hold) move between pockets, swapping if taken. */
  async function dropInto(cardId: string, slot: number) {
    setBusyId(cardId);
    setNotice(null);
    const before = rows.find((r) => r.id === cardId);
    try {
      const res = (await placeFn({ data: { id: cardId, slot } })) as { swapped: boolean };
      await refresh();
      if (before) {
        setLastMove({
          id: before.id,
          name: before.card_name,
          status: before.status as PullStatus,
          slot: before.binder_slot,
          swapped: res.swapped,
        });
      }
      setNotice(res.swapped ? `Cards swapped — pocket #${slot + 1} updated.` : `Moved into pocket #${slot + 1}.`);
    } catch (e) {
      setNotice(e instanceof Error ? e.message : "That move did not save.");
    } finally {
      setBusyId(null);
      setPlacing(null);
    }
  }

  /** Put the last moved card back where it came from. */
  async function undoLastMove() {
    if (!lastMove) return;
    const target = lastMove;
    setLastMove(null);
    setBusyId(target.id);
    setNotice(null);
    try {
      if (target.status === "binder" && target.slot !== null) {
        await placeFn({ data: { id: target.id, slot: target.slot } });
      } else {
        await statusFn({ data: { id: target.id, status: target.status, slot: target.slot } });
      }
      await refresh();
      setNotice(
        `Undone — ${target.name} is back ${
          target.status === "binder" && target.slot !== null
            ? `in pocket #${target.slot + 1}`
            : target.status === "new"
              ? "in your new pulls"
              : target.status === "trade"
                ? "in your trade stack"
                : "where it was"
        }.`,
      );
    } catch (e) {
      setNotice(e instanceof Error ? e.message : "That undo did not save.");
    } finally {
      setBusyId(null);
    }
  }





  async function shareBinder() {
    if (!session) return;
    let url = `https://palstars.com/shared-binder/${session.user.id}`;
    let text = `My Palworld card binder on Pal Stars is complete — ${BINDER_CAPACITY} cards filed. Take a look (Pal Stars account required):`;
    setNotice(null);
    let minted = false;
    try {
      // Mint a secret, expiring share link so anyone with the link can view
      // the binder without an account — and it can be revoked later.
      const { token } = await mintShareLink({ data: { label: "Binder share link" } });
      url = `https://palstars.com/binder-share/${token}`;
      text = `My Palworld card binder on Pal Stars is complete — ${BINDER_CAPACITY} cards filed. Private link, no account needed:`;
      minted = true;
    } catch (e) {
      // Most common cause: the 5-active-link cap. Tell the member, then fall
      // back to the member-only binder URL so sharing still works.
      const msg = e instanceof Error ? e.message : "";
      setNotice(
        /active binder share links/i.test(msg)
          ? "You already have 5 active share links. Revoke one below to mint a fresh private link — sharing the member-only link instead."
          : "Could not mint a private link — sharing the member-only binder link instead."
      );
    }

    try {
      if (typeof navigator !== "undefined" && navigator.share) {
        await navigator.share({ title: "My Palworld card binder", text, url });
      } else {
        await navigator.clipboard.writeText(`${text} ${url}`);
        if (minted) {
          setNotice("Private binder link copied — it expires in 30 days and you can revoke it any time.");
        }
      }
    } catch {

      setNotice("Sharing was cancelled.");
    }
  }

  if (loading) return <div className="mx-auto max-w-3xl px-4 py-16 text-muted-foreground">Loading your binder…</div>;

  if (!session) {
    const demoMap = new Map<number, CardRow>(DEMO_BINDER.map((c: DemoCard) => [c.binder_slot as number, c as CardRow]));
    return (
      <div className="mx-auto max-w-5xl px-4 sm:px-6 py-10 space-y-8">
        <header className="text-center">
          <p className="text-xs uppercase tracking-widest text-primary">Pal Stars collector binder</p>
          <h1 className="mt-2 text-3xl font-bold">Your Palworld card binder</h1>
          <p className="aeo-answer mx-auto mt-3 max-w-2xl text-sm text-muted-foreground">
            The collector binder is part of your free Pal Stars account: {BINDER_CAPACITY} pockets across {BINDER_PAGES}{" "}
            nine-pocket pages sorted by card color, plus a trade stack and 12 free booster packs every day. Here is exactly what page 1 looks
            like once you start filing pulls.
          </p>
          <div className="mt-5 flex flex-wrap justify-center gap-2">
            <Link to="/auth" className="rounded-md bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground hover:brightness-110">
              Create a free account
            </Link>
            <Link to="/pack-opening" className="rounded-md border px-5 py-2.5 text-sm font-semibold hover:bg-card">
              Try a free pack first
            </Link>
          </div>
        </header>

        <section>
          <h2 className="text-xl font-bold">Example binder — page 1 of {BINDER_PAGES}</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Nine pockets per page. Every card shows its rarity and grade — hover or tap the grade to see the sub-grades and the exact card issues a grader noted; empty pockets stay as faded pack art until you
            fill them.
          </p>
          <div className="glass mt-4 rounded-2xl p-4 sm:p-6">
            <BinderGrid page={0} cards={demoMap} />
          </div>
          <div className="glass mt-4 rounded-2xl p-4 sm:p-6">
            <p className="mb-3 text-sm font-semibold">Page 2 — still to fill</p>
            <BinderGrid page={1} cards={new Map()} />
          </div>
        </section>
      </div>
    );
  }


  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 py-8 space-y-10">
      <header>
        <p className="text-xs uppercase tracking-widest text-primary">Pal Stars collector binder</p>
        <div className="mt-2 flex flex-wrap items-start justify-between gap-3">
          <h1 className="text-3xl font-bold">Your Palworld card binder</h1>
          <ShareButton title="My Palworld card binder — Pal Stars" text="My Palworld TCG collector binder on Pal Stars." path="/binder" />
        </div>
        <p className="mt-2 rounded-xl border border-border/60 bg-card/50 px-4 py-3 text-sm">
          <span className="text-muted-foreground">Collection value (live market)</span>{" "}
          <strong className="text-lg text-primary">${netWorth.toFixed(2)}</strong>{" "}
          <span className="text-xs text-muted-foreground">
            across {valuedCount} priced card{valuedCount === 1 ? "" : "s"} — averages from the{" "}
            <Link to="/prices" className="font-semibold text-primary hover:underline">Palworld TCG Price Chart</Link>
          </span>
        </p>
        <p className="aeo-answer mt-3 text-sm text-muted-foreground">
          {filled} of {BINDER_CAPACITY} pockets filled across {BINDER_PAGES} nine-pocket pages, sorted into Red, Blue,
          Green, Purple and Colorless color blocks with a dedicated chase page for SSP, OSR and SSS cards. Drag a card
          to any pocket to rearrange it — drop it on an occupied pocket and the two cards swap. On a phone, press and
          hold a card, then tap the pocket you want.
        </p>
        <p className="mt-3 text-sm">
          <Link to="/binder-rewards" className="font-semibold text-primary hover:underline">
            Binder rewards — claim free booster packs for collection milestones →
          </Link>
        </p>
        <p className="mt-1 text-sm">
          <Link to="/trades" className="font-semibold text-primary hover:underline">
            Card trading — cards you mark "for trade" are listed for other collectors to swap →
          </Link>
        </p>


        <div className="mt-3 h-2 w-full max-w-sm overflow-hidden rounded-full bg-border">
          <div className="h-full rounded-full bg-primary" style={{ width: `${(filled / BINDER_CAPACITY) * 100}%` }} />
        </div>
      </header>

      <div aria-live="polite" className="flex flex-wrap items-center gap-3">
        {notice && <p className="text-sm text-primary">{notice}</p>}
        {lastMove && (
          <button
            type="button"
            onClick={() => void undoLastMove()}
            disabled={busyId !== null}
            className="rounded-md border border-primary/60 px-3 py-1.5 text-xs font-semibold text-primary hover:bg-primary/10 disabled:opacity-50"
          >
            ↩ Undo move
          </button>
        )}
      </div>


      <section>
        <BinderCover filled={filled} />

        {/* Color section tabs — also drop targets: on desktop, drag a card over a
            tab (or a page arrow) to jump there, then drop it in the pocket you want. */}
        <div className="mt-4 flex flex-wrap gap-2">
          {BINDER_SECTIONS.map((s) => {
            const active = s.pages.includes(page);
            const count = slotsOfSection(s).filter((slot) => binderMap.has(slot)).length;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => setPage(s.pages[0])}
                onDragOver={(e) => { e.preventDefault(); if (!active) flipTo(() => s.pages[0]); }}
                onDrop={(e) => { e.preventDefault(); setPage(s.pages[0]); }}
                aria-pressed={active}
                title={`Jump to ${s.label} — drag a card here to move it to this section`}
                className="rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors"
                style={{
                  borderColor: s.tint,
                  color: active ? "#0b1220" : s.tint,
                  background: active ? s.tint : "transparent",
                }}
              >
                {s.label} · {count}/{s.pages.length * BINDER_SLOTS_PER_PAGE}
              </button>
            );
          })}
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-xl font-bold">
              Page {page + 1} of {BINDER_PAGES} —{" "}
              <span style={{ color: sectionForSlot(page * BINDER_SLOTS_PER_PAGE).tint }}>
                {sectionForSlot(page * BINDER_SLOTS_PER_PAGE).label}
              </span>
            </h2>
            <p className="mt-1 text-xs text-muted-foreground">{sectionForSlot(page * BINDER_SLOTS_PER_PAGE).blurb}</p>
            <p className="mt-1 hidden text-xs text-muted-foreground sm:block">
              Desktop tip: drag any card onto a color tab or page arrow to turn the page mid-drag, then drop it into the
              pocket you want. Occupied pockets swap automatically.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              onDragOver={(e) => { e.preventDefault(); flipTo((p) => p - 1); }}
              onDrop={(e) => e.preventDefault()}
              disabled={page === 0}
              title="Previous page — drag a card here to turn back"
              className="rounded-md border px-3 py-1.5 text-sm font-semibold hover:bg-card disabled:opacity-40"
            >
              ← Previous page
            </button>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(BINDER_PAGES - 1, p + 1))}
              onDragOver={(e) => { e.preventDefault(); flipTo((p) => p + 1); }}
              onDrop={(e) => e.preventDefault()}
              disabled={page >= BINDER_PAGES - 1}
              title="Next page — drag a card here to turn forward"
              className="rounded-md border px-3 py-1.5 text-sm font-semibold hover:bg-card disabled:opacity-40"
            >
              Next page →
            </button>
          </div>
        </div>

        {placing && (
          <p className="mt-3 rounded-lg border border-primary/50 p-3 text-sm">
            Placing <strong>{placing.card_name}</strong> — tap an empty pocket on this page, or{" "}
            <button type="button" onClick={() => setPlacing(null)} className="text-primary hover:underline">cancel</button>.
          </p>
        )}

        {/* Binder filters — highlight the cards you're looking for */}
        <div className="glass mt-4 flex flex-wrap items-end gap-3 rounded-xl p-3">
          <label className="text-xs">
            <span className="block text-muted-foreground">Search this binder</span>
            <input
              value={fq}
              onChange={(e) => setFq(e.target.value)}
              placeholder="Name or card code"
              className="mt-1 w-48 rounded-md border bg-background px-3 py-2 text-sm"
            />
          </label>
          <label className="text-xs">
            <span className="block text-muted-foreground">Rarity</span>
            <select value={fRarity} onChange={(e) => setFRarity(e.target.value)} className="mt-1 rounded-md border bg-background px-3 py-2 text-sm">
              <option value="all">All rarities</option>
              {rarities.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </label>
          <label className="text-xs">
            <span className="block text-muted-foreground">Minimum grade</span>
            <select value={fGrade} onChange={(e) => setFGrade(e.target.value)} className="mt-1 rounded-md border bg-background px-3 py-2 text-sm">
              <option value="all">Any grade</option>
              <option value="10">Gem Mint 10</option>
              <option value="9">9.0 and up</option>
              <option value="8">8.0 and up</option>
            </select>
          </label>
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={fFav} onChange={(e) => setFFav(e.target.checked)} className="h-4 w-4" />
            <span>Favourites only</span>
          </label>
          <p className="text-xs text-muted-foreground" aria-live="polite">
            {filtersOn ? `${matchCount} of ${filled} filed cards match` : `${filled} cards filed`}
          </p>
          {filtersOn && (
            <button
              type="button"
              onClick={() => { setFq(""); setFRarity("all"); setFGrade("all"); setFFav(false); }}
              className="rounded-md border px-3 py-1.5 text-xs font-semibold hover:bg-card"
            >
              Clear filters
            </button>
          )}
        </div>

        <div
          className="glass mt-4 rounded-2xl p-4 sm:p-6"
          style={{ boxShadow: `inset 0 0 0 2px ${sectionForSlot(page * BINDER_SLOTS_PER_PAGE).tint}33` }}
        >
          <BinderGrid
            page={page}
            cards={binderMap}
            onExpand={(c) => setZoom(c)}
            onTrade={(c) => void move(c, "trade")}
            onRemove={(c) => void move(c, "discarded")}
            onFavorite={(c) => void toggleFavorite(c)}

            onDropCard={(cardId, slot) => void dropInto(cardId, slot)}
            onPickSlot={placing ? (slot) => void dropInto(placing.id, slot) : undefined}
            onHoldCard={(slot) => setPlacing(binderMap.get(slot) ?? null)}
            isMatch={filtersOn ? isMatch : undefined}
          />
        </div>



        <div className="mt-3 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={shareBinder}
            disabled={!complete}
            title={complete ? "Share your completed binder" : "Fill every pocket to unlock binder sharing"}
            className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:brightness-110 disabled:opacity-40"
          >
            Share completed binder
          </button>
          <span className="text-xs text-muted-foreground">
            {complete
              ? "Binder complete — the share link opens for Pal Stars account holders only."
              : `${BINDER_CAPACITY - filled} pockets left before your binder can be shared.`}
          </span>
        </div>
      </section>

      <section>
        <h2 className="text-xl font-bold">New pulls to sort ({unsorted.length})</h2>
        {unsorted.length === 0 ? (
          <p className="mt-2 text-sm text-muted-foreground">
            Nothing waiting. <Link to="/pack-opening" className="text-primary hover:underline">Open today's free packs →</Link>
          </p>
        ) : (
          <ul className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {unsorted.slice(0, 60).map((c) => (
              <li key={c.id} className="glass rounded-xl p-3">
                <div className="flex gap-3">
                  <CardImage src={c.image} alt={`${c.card_name} ${c.card_code} ${c.rarity} Palworld card`} width={70} height={98} className="rounded" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold">{c.card_name}</p>
                    <p className="text-xs text-muted-foreground">
                      <span style={{ color: RARITY_GLOW[c.rarity] }}>{c.rarity}</span> ·{" "}
                      <GradeBadge card={c}>{c.grade_label}</GradeBadge>
                    </p>
                    <p className="mt-1 font-mono text-[10px] text-muted-foreground">{c.card_code}</p>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button type="button" disabled={busyId === c.id} onClick={() => void fileInColorSection(c)} className="rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:brightness-110 disabled:opacity-50">
                    File in color section
                  </button>
                  <button type="button" disabled={busyId === c.id} onClick={() => setPlacing(c)} className="rounded-md border border-primary/60 px-3 py-1.5 text-xs font-semibold text-primary hover:bg-primary/10 disabled:opacity-50">
                    Choose pocket
                  </button>

                  <button type="button" disabled={busyId === c.id} onClick={() => move(c, "trade")} className="rounded-md border px-3 py-1.5 text-xs font-semibold hover:bg-card disabled:opacity-50">
                    Store for trade
                  </button>
                  <button type="button" disabled={busyId === c.id} onClick={() => move(c, "discarded")} className="rounded-md border px-3 py-1.5 text-xs font-semibold text-muted-foreground hover:text-destructive disabled:opacity-50">
                    Discard
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section>
        <h2 className="text-xl font-bold">Trade stack ({tradeStack.length})</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Cards you are holding back for future trades. Move one into the binder any time, or take it to the{" "}
          <Link to="/pack-opening" className="text-primary hover:underline">daily top-pull board</Link> to start a trade.
        </p>
        {tradeStack.length > 0 && (
          <ul className="mt-4 grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-6 gap-3">
            {tradeStack.slice(0, 60).map((c) => (
              <li key={c.id} className="text-center">
                <CardHoverZoom
                  src={c.image}
                  alt={`${c.card_name} ${c.card_code} ${c.rarity} Palworld card`}
                  width={140}
                  height={195}
                  scale={1.2}
                  onExpand={() => setZoom(c)}
                />
                <p className="mt-1 truncate text-[11px] font-semibold">{c.card_name}</p>
                <button type="button" onClick={() => move(c, "binder")} className="mt-1 text-[10px] text-primary hover:underline">
                  Move to binder
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {zoom && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={`${zoom.card_name} full size card`}
          onClick={() => setZoom(null)}
          className="fixed inset-0 z-[80] grid place-items-center bg-background/90 p-4"
        >
          <PannableCard label={`${zoom.card_name} ${zoom.card_code}`}>
            <CardImage src={zoom.image} alt={`${zoom.card_name} ${zoom.card_code} full size`} width={520} height={726} priority responsive className="max-h-[88vh] w-auto" />
          </PannableCard>
        </div>
      )}
    </div>
  );
}
